import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { redApi } from '../../services/api'
import { RedProfessionista } from '../../types/red'

interface Props {
  tipoIniziale?: string
  tipoBloccato?: boolean
  professionistaEsistente?: RedProfessionista
  onClose: () => void
  onCreated: (professionista: RedProfessionista) => void
}

const TIPI = [
  { value: 'tecnico', label: 'Tecnico / Ufficio tecnico' },
  { value: 'notaio', label: 'Notaio' },
  { value: 'impresa', label: 'Impresa' },
]

// Bug #9 + #13: creazione al volo di un professionista/ufficio tecnico con
// l'anagrafica completa richiesta dall'utente (foto elenco professionisti).
// Riusato anche in modalità modifica dalla Rubrica Professionisti.
const NuovoProfessionistaModal: React.FC<Props> = ({ tipoIniziale = 'tecnico', tipoBloccato, professionistaEsistente, onClose, onCreated }) => {
  const p = professionistaEsistente
  const [form, setForm] = useState({
    tipo: p?.tipo ?? tipoIniziale,
    professione: p?.professione ?? '',
    nome: p?.nome ?? '',
    ruolo: p?.ruolo ?? '',
    indirizzo: p?.indirizzo ?? '',
    comune: p?.comune ?? '',
    provincia: p?.provincia ?? '',
    referente: p?.referente ?? '',
    email: p?.email ?? '',
    telefono: p?.telefono ?? '',
    cellulare: p?.cellulare ?? '',
    iban: p?.iban ?? '',
    iban2: p?.iban2 ?? '',
    note: p?.note ?? '',
    varie_interne: p?.varie_interne ?? '',
  })
  const [saving, setSaving] = useState(false)

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }))

  const handleSalva = async () => {
    if (!form.nome.trim()) {
      toast.error('Il nome è obbligatorio')
      return
    }
    setSaving(true)
    try {
      // tipo/ruolo/nome sono obbligatori lato backend (RedProfessionistaCreate):
      // non vanno convertiti in undefined se vuoti, altrimenti il campo sparisce
      // dal body e l'API risponde 422 senza un messaggio comprensibile in UI
      const CAMPI_OBBLIGATORI = new Set(['tipo', 'ruolo', 'nome'])
      const payload: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(form)) {
        payload[k] = (v === '' && !CAMPI_OBBLIGATORI.has(k)) ? undefined : v
      }
      const res = professionistaEsistente
        ? await redApi.professionisti.update(professionistaEsistente.id, payload)
        : await redApi.professionisti.create(payload)
      toast.success(professionistaEsistente ? 'Professionista aggiornato' : 'Professionista creato')
      onCreated(res.data)
      onClose()
    } catch {
      toast.error(professionistaEsistente ? 'Errore aggiornamento professionista' : 'Errore creazione professionista')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !saving && onClose()}>
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">{professionistaEsistente ? 'Modifica professionista' : 'Nuovo professionista'}</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label text-xs">Tipo</label>
            <select className="input" value={form.tipo} onChange={e => set('tipo', e.target.value)} disabled={tipoBloccato}>
              {TIPI.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Professione</label>
            <input className="input" value={form.professione} onChange={e => set('professione', e.target.value)} placeholder="Dott., Geom., Arch., Ing..." />
          </div>
          <div>
            <label className="label text-xs">Ruolo</label>
            <input className="input" value={form.ruolo} onChange={e => set('ruolo', e.target.value)} placeholder="Ingegnere, Geometra..." />
          </div>
          <div className="col-span-3">
            <label className="label text-xs">Nome *</label>
            <input className="input" value={form.nome} onChange={e => set('nome', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label text-xs">Indirizzo</label>
            <input className="input" value={form.indirizzo} onChange={e => set('indirizzo', e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">Città</label>
            <input className="input" value={form.comune} onChange={e => set('comune', e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">Provincia</label>
            <input className="input" value={form.provincia} onChange={e => set('provincia', e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">Referente</label>
            <input className="input" value={form.referente} onChange={e => set('referente', e.target.value)} placeholder="Persona di contatto" />
          </div>
          <div>
            <label className="label text-xs">Email</label>
            <input className="input" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">Tel.</label>
            <input className="input" value={form.telefono} onChange={e => set('telefono', e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">Tel. 2</label>
            <input className="input" value={form.cellulare} onChange={e => set('cellulare', e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">IBAN 1</label>
            <input className="input" value={form.iban} onChange={e => set('iban', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label text-xs">IBAN 2</label>
            <input className="input" value={form.iban2} onChange={e => set('iban2', e.target.value)} />
          </div>
          <div className="col-span-3">
            <label className="label text-xs">Note</label>
            <textarea className="input" rows={2} value={form.note} onChange={e => set('note', e.target.value)} />
          </div>
          <div className="col-span-3">
            <label className="label text-xs">Varie interne</label>
            <textarea className="input" rows={2} value={form.varie_interne} onChange={e => set('varie_interne', e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleSalva} disabled={saving} className="btn-primary text-sm">
            {saving ? 'Salvataggio...' : (professionistaEsistente ? 'Salva modifiche' : 'Crea professionista')}
          </button>
          <button onClick={onClose} disabled={saving} className="text-sm text-gray-500">Annulla</button>
        </div>
      </div>
    </div>
  )
}

export default NuovoProfessionistaModal
